import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { Typography } from '@design-system/components/typography/typography';
import { SearchInput } from '@design-system/components/searchinput';
import { faCheck, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { Checkbox } from '@design-system/components/checkbox/checkbox';
import { clsx } from 'clsx';
import { sprinkles } from '@design-system/sprinkles.css';
import { isEmpty } from 'lodash';
import {
  BundleSectionList,
  BundlesTable,
} from '../viewModel/subscriptionBundle.vm.mapper';
import { TooltipWrapper } from '../../view/TooltipWrapper';

export const BundlesContentTable = (p: {
  list: BundlesTable;
  searchText: string;
  search: (value: string) => void;
  editMode: boolean;
}) => {
  return (
    <div className="flex flex-col bg-white rounded-[10px] gap-3 pb-2">
      <TableAccordion header={p.list.header}>
        <ContentSections {...p} />
      </TableAccordion>
    </div>
  );
};

export const ContentSections = ({
  list,
  searchText,
  search,
  editMode,
}: {
  list: BundlesTable;
  searchText: string;
  search: (value: string) => void;
  editMode: boolean;
}) => {
  return (
    <div className="flex flex-col gap-3">
      <div className="max-w-[300px] px-4">
        <SearchInput
          placeholder="Search by name"
          value={searchText}
          onChange={(e) => search(e.target.value)}
          onClear={() => search('')}
        />
      </div>
      {searchText &&
      isEmpty(list.features.list) &&
      isEmpty(list.programs.list) ? (
        <div className="flex flex-col justify-center items-center w-full h-full">
          <div className="flex gap-1">
            <Typography className={sprinkles({ color: 'N-80' })}>
              No results found for:
            </Typography>
            <Typography className="text-black">{searchText}</Typography>
          </div>
          <Typography className={sprinkles({ color: 'N-80' })}>
            Please try searching with another term.
          </Typography>
        </div>
      ) : null}
      <div className="flex flex-col bg-white gap-4">
        {isEmpty(list.features.list) ? null : (
          <SectionAccordion title="Features">
            <ListWithSelect list={list.features.list} editMode={editMode} />
          </SectionAccordion>
        )}
        {isEmpty(list.programs.list) ? null : (
          <SectionAccordion title="Programs">
            <ListWithSelect list={list.programs.list} editMode={editMode} />
          </SectionAccordion>
        )}
        {isEmpty(list.content.list) ? null : (
          <SectionAccordion title="Content Items">
            <ListWithSelect list={list.content.list} editMode={editMode} />
          </SectionAccordion>
        )}
      </div>
    </div>
  );
};

const TableAccordion: React.FC<
  React.PropsWithChildren<{ header: BundlesTable['header'] }>
> = ({ header: { bundleName, section }, children }) => (
  <div className="flex flex-col gap-4 px-2 w-full">
    <div
      className="flex gap-4 items-center justify-between rounded-md px-[25px] py-1 w-full h-[52px] cursor-pointer"
      onClick={section.toggle}
    >
      <div className="flex items-center gap-2">
        <FontAwesomeIcon
          icon={section.open ? faChevronUp : faChevronDown}
          className={sprinkles({ color: 'N-80' })}
        />
        <Typography variant="body" size="medium">
          {section.open ? 'Close' : 'Open'}
        </Typography>
      </div>
      <div className="flex gap-4 md:gap-16 pr-2">
        {bundleName.map((name) => (
          <Typography
            key={name}
            className="w-[80px] md:w-[125px] text-wrap"
            variant="body"
            size="large"
          >
            {name}
          </Typography>
        ))}
      </div>
    </div>
    {section.open && children}
  </div>
);

const SectionAccordion: React.FC<
  React.PropsWithChildren<{ title: string }>
> = ({ title, children }) => {
  const [open, setOpen] = useState(true);

  return (
    <div className="flex flex-col gap-4 px-2">
      <div
        className="flex gap-4 items-center hover:bg-[#EEE] rounded-md px-[25px] py-1"
        onClick={() => setOpen((prev) => !prev)}
      >
        <FontAwesomeIcon
          icon={open ? faChevronDown : faChevronUp}
          className={sprinkles({ color: 'N-80' })}
        />
        <Typography
          variant="body"
          size="small"
          className={sprinkles({ color: 'N-80' })}
        >
          {title}
        </Typography>
        <hr className="w-full" />
      </div>
      {open && children}
    </div>
  );
};

const ListWithSelect = ({
  list,
  editMode,
}: {
  list: BundleSectionList;
  editMode: boolean;
}) => (
  <div className="flex flex-col gap-4">
    {list.map((row) => (
      <div
        key={row.name}
        className="flex justify-between px-8 py-1 hover:bg-[#F8F8F8] rounded-md"
      >
        <div>
          <Typography variant="body" size="medium">
            {row.name}
          </Typography>
        </div>
        <div className="flex gap-4 md:gap-16">
          {row.bundleContent.map((b, i) => (
            <div key={i} className="w-[80px] md:w-[125px] flex justify-center">
              {editMode ? (
                <TooltipWrapper tooltip={b.disabled ? b.tooltip : undefined}>
                  <Checkbox
                    checkboxVariant="neutral"
                    checked={b.checked}
                    onChange={b.onChange}
                    disabled={b.disabled}
                  />
                </TooltipWrapper>
              ) : (
                <FontAwesomeIcon
                  icon={b.checked ? faCheck : faXmark}
                  className={clsx(
                    b.checked
                      ? sprinkles({ color: 'N-100' })
                      : sprinkles({ color: 'N-40' }),
                  )}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);
